import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Globe, Users, CheckCircle, RefreshCw, X, Phone } from "lucide-react";
import axios from "axios";

// Country codes with flag emojis - common countries first
const COUNTRY_CODES = [
  { code: "+1", country: "US", label: "+1 (US)" },
  { code: "+44", country: "GB", label: "+44 (UK)" },
  { code: "+61", country: "AU", label: "+61 (AU)" },
  { code: "+91", country: "IN", label: "+91 (IN)" },
  { code: "+49", country: "DE", label: "+49 (DE)" },
  { code: "+33", country: "FR", label: "+33 (FR)" },
  { code: "+81", country: "JP", label: "+81 (JP)" },
  { code: "+86", country: "CN", label: "+86 (CN)" },
  { code: "+55", country: "BR", label: "+55 (BR)" },
  { code: "+52", country: "MX", label: "+52 (MX)" },
  { code: "+34", country: "ES", label: "+34 (ES)" },
  { code: "+39", country: "IT", label: "+39 (IT)" },
  { code: "+31", country: "NL", label: "+31 (NL)" },
  { code: "+82", country: "KR", label: "+82 (KR)" },
  { code: "+7", country: "RU", label: "+7 (RU)" },
  { code: "+27", country: "ZA", label: "+27 (ZA)" },
  { code: "+971", country: "AE", label: "+971 (AE)" },
  { code: "+966", country: "SA", label: "+966 (SA)" },
  { code: "+65", country: "SG", label: "+65 (SG)" },
  { code: "+60", country: "MY", label: "+60 (MY)" },
  { code: "+63", country: "PH", label: "+63 (PH)" },
  { code: "+62", country: "ID", label: "+62 (ID)" },
  { code: "+66", country: "TH", label: "+66 (TH)" },
  { code: "+84", country: "VN", label: "+84 (VN)" },
  { code: "+92", country: "PK", label: "+92 (PK)" },
  { code: "+880", country: "BD", label: "+880 (BD)" },
  { code: "+234", country: "NG", label: "+234 (NG)" },
  { code: "+254", country: "KE", label: "+254 (KE)" },
  { code: "+20", country: "EG", label: "+20 (EG)" },
  { code: "+212", country: "MA", label: "+212 (MA)" },
  { code: "+90", country: "TR", label: "+90 (TR)" },
  { code: "+48", country: "PL", label: "+48 (PL)" },
  { code: "+46", country: "SE", label: "+46 (SE)" },
  { code: "+47", country: "NO", label: "+47 (NO)" },
  { code: "+45", country: "DK", label: "+45 (DK)" },
  { code: "+358", country: "FI", label: "+358 (FI)" },
  { code: "+41", country: "CH", label: "+41 (CH)" },
  { code: "+43", country: "AT", label: "+43 (AT)" },
  { code: "+32", country: "BE", label: "+32 (BE)" },
  { code: "+353", country: "IE", label: "+353 (IE)" },
  { code: "+351", country: "PT", label: "+351 (PT)" },
  { code: "+30", country: "GR", label: "+30 (GR)" },
  { code: "+420", country: "CZ", label: "+420 (CZ)" },
  { code: "+36", country: "HU", label: "+36 (HU)" },
  { code: "+40", country: "RO", label: "+40 (RO)" },
  { code: "+380", country: "UA", label: "+380 (UA)" },
  { code: "+972", country: "IL", label: "+972 (IL)" },
  { code: "+64", country: "NZ", label: "+64 (NZ)" },
  { code: "+54", country: "AR", label: "+54 (AR)" },
  { code: "+56", country: "CL", label: "+56 (CL)" },
  { code: "+57", country: "CO", label: "+57 (CO)" },
  { code: "+51", country: "PE", label: "+51 (PE)" },
  { code: "+58", country: "VE", label: "+58 (VE)" },
  { code: "+1787", country: "PR", label: "+1787 (PR)" },
];

// Country list with USA first
const COUNTRIES = [
  "United States",
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Chad", "Chile", "China",
  "Colombia", "Comoros", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark",
  "Djibouti", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Estonia", "Ethiopia", "Fiji",
  "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Guatemala",
  "Guinea", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Liberia", "Libya", "Lithuania", "Luxembourg", "Madagascar", "Malawi",
  "Malaysia", "Maldives", "Mali", "Malta", "Mauritania", "Mauritius", "Mexico", "Moldova", "Monaco",
  "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman",
  "Pakistan", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar", "Romania", "Russia", "Rwanda", "Saudi Arabia", "Senegal", "Serbia", "Sierra Leone",
  "Singapore", "Slovakia", "Slovenia", "Somalia", "South Africa", "South Korea", "Spain", "Sri Lanka",
  "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo",
  "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Uganda", "Ukraine", "United Arab Emirates",
  "United Kingdom", "Uruguay", "Uzbekistan", "Venezuela", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe"
];

const SOCIAL_PLATFORMS = [
  { id: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourprofile" },
  { id: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/in/yourprofile" },
  { id: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourusername" }
];

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneCountryCode: "+1", // Default to US
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    country: "United States", // Default to USA
    socialProfiles: {
      facebook: { enabled: false, url: "" },
      linkedin: { enabled: false, url: "" },
      instagram: { enabled: false, url: "" }
    },
    agreedToTerms: false,
    consentTransactional: false,
    consentMarketing: false
  });
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSocialToggle = (platform) => {
    setFormData({
      ...formData,
      socialProfiles: {
        ...formData.socialProfiles,
        [platform]: {
          ...formData.socialProfiles[platform],
          enabled: !formData.socialProfiles[platform].enabled
        }
      }
    });
  };

  const handleSocialUrlChange = (platform, url) => {
    setFormData({
      ...formData,
      socialProfiles: {
        ...formData.socialProfiles,
        [platform]: {
          ...formData.socialProfiles[platform],
          url
        }
      }
    });
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      toast.error("First name is required");
      return false;
    }
    if (!formData.lastName.trim()) {
      toast.error("Last name is required");
      return false;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      toast.error("Phone number is required");
      return false;
    }
    // Basic phone number validation (digits only, 7-15 chars)
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      toast.error("Please enter a valid phone number (7-15 digits)");
      return false;
    }
    if (!formData.country) {
      toast.error("Country is required");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    
    // Password strength validation
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      toast.error("Password must contain at least one uppercase letter");
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      toast.error("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      toast.error("Password must contain at least one number");
      return false;
    }
    
    // Check at least one social profile
    const enabledProfiles = Object.entries(formData.socialProfiles)
      .filter(([_, profile]) => profile.enabled && profile.url.trim());
    
    if (enabledProfiles.length === 0) {
      toast.error("At least one social media profile is required");
      return false;
    }

    // Check terms agreement
    if (!formData.agreedToTerms) {
      toast.error("You must agree to the Terms of Use and Privacy Policy");
      return false;
    }

    // Check transactional consent (required)
    if (!formData.consentTransactional) {
      toast.error("You must consent to receive transactional messages");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    // Build social profiles array
    const socialProfiles = Object.entries(formData.socialProfiles)
      .filter(([_, profile]) => profile.enabled && profile.url.trim())
      .map(([platform, profile]) => ({
        platform,
        url: profile.url.trim()
      }));

    try {
      const response = await register(
        formData.email,
        formData.password,
        `${formData.firstName} ${formData.lastName}`,
        "worker",
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          country: formData.country,
          phone_country_code: formData.phoneCountryCode,
          phone_number: formData.phoneNumber.replace(/\D/g, ''), // Store digits only
          full_phone: `${formData.phoneCountryCode}${formData.phoneNumber.replace(/\D/g, '')}`,
          social_profiles: socialProfiles,
          consent_transactional: formData.consentTransactional,
          consent_marketing: formData.consentMarketing
        }
      );
      
      // New flow: Show verification email message instead of redirecting
      if (response.requires_verification) {
        setRegisteredEmail(formData.email);
        setRegistrationSuccess(true);
        toast.success("Check your email to verify your account!");
      } else {
        // Fallback for any other flow
        toast.success("Registration successful!");
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/v2/auth/resend-verification?email=${encodeURIComponent(registeredEmail)}`);
      toast.success("Verification email sent! Please check your inbox.");
    } catch (error) {
      toast.error("Failed to resend verification email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show success state after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
            <Mail className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
            Check Your Email!
          </h2>
          <p className="text-slate-600 mb-2">
            We've sent a verification link to:
          </p>
          <p className="text-emerald-600 font-semibold mb-6">
            {registeredEmail}
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Click the link in the email to verify your account and start earning with GigsEasy.
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={handleResendVerification}
              disabled={loading}
              variant="outline"
              className="w-full"
              data-testid="resend-verification-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? "Sending..." : "Resend Verification Email"}
            </Button>
            
            <Link to="/login" className="block">
              <Button
                className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </Link>
          </div>
          
          <p className="mt-6 text-xs text-slate-400">
            Didn't receive the email? Check your spam folder or click resend above.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:block flex-1 bg-slate-50">
        <div className="h-full w-full relative">
          <img
            src="https://images.unsplash.com/photo-1765648684671-db09402b2d48?w=1200&q=90"
            alt="Woman smiling while working on laptop"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
          <div className="absolute bottom-12 left-12 right-12">
            <h2 className="text-3xl font-heading font-bold text-white mb-4">
              Join Thousands of Gigsters
            </h2>
            <p className="text-slate-300">
              Start earning today by completing simple tasks. No special skills required.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">GigsEasy</span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Create account</h1>
          <p className="text-slate-600 mb-6">Start earning rewards today</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-slate-700 font-medium">First Name *</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    className="pl-12 h-11 border-2 border-slate-200 focus:border-primary"
                    data-testid="register-first-name-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-slate-700 font-medium">Last Name *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  className="h-11 border-2 border-slate-200 focus:border-primary"
                  data-testid="register-last-name-input"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-12 h-11 border-2 border-slate-200 focus:border-primary"
                  data-testid="register-email-input"
                  required
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber" className="text-slate-700 font-medium">Phone Number *</Label>
              <div className="flex gap-2">
                <Select 
                  value={formData.phoneCountryCode} 
                  onValueChange={(value) => setFormData({ ...formData, phoneCountryCode: value })}
                >
                  <SelectTrigger className="w-[140px] h-11 border-2 border-slate-200 focus:border-primary" data-testid="register-phone-code-select">
                    <SelectValue placeholder="+1" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {COUNTRY_CODES.map((item, index) => (
                      <SelectItem key={`${item.code}-${item.country}-${index}`} value={item.code}>
                        {item.code} ({item.country})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="pl-12 h-11 border-2 border-slate-200 focus:border-primary"
                    data-testid="register-phone-input"
                    required
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">We'll use this number for account verification and important updates</p>
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label htmlFor="country" className="text-slate-700 font-medium">Country *</Label>
              <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                <SelectTrigger className="h-11 border-2 border-slate-200 focus:border-primary" data-testid="register-country-select">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-400" />
                    <SelectValue placeholder="Select your country" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Social Media Profiles */}
            <div className="space-y-3">
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Social Media Profiles *
              </Label>
              <p className="text-sm text-slate-500">Select at least one and provide your profile URL</p>
              <p className="text-sm text-amber-600 font-medium bg-amber-50 p-2 rounded border border-amber-200">
                Providing social media profile will increase your approval chances
              </p>
              
              <div className="space-y-3">
                {SOCIAL_PLATFORMS.map((platform) => (
                  <div key={platform.id} className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`social-${platform.id}`}
                        checked={formData.socialProfiles[platform.id].enabled}
                        onCheckedChange={() => handleSocialToggle(platform.id)}
                        data-testid={`social-${platform.id}-checkbox`}
                      />
                      <Label htmlFor={`social-${platform.id}`} className="text-sm font-medium cursor-pointer">
                        {platform.label}
                      </Label>
                    </div>
                    {formData.socialProfiles[platform.id].enabled && (
                      <Input
                        type="text"
                        value={formData.socialProfiles[platform.id].url}
                        onChange={(e) => handleSocialUrlChange(platform.id, e.target.value)}
                        placeholder={platform.placeholder}
                        className="h-10 border-2 border-slate-200 focus:border-primary text-sm"
                        data-testid={`social-${platform.id}-url`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  className="pl-12 h-11 border-2 border-slate-200 focus:border-primary"
                  data-testid="register-password-input"
                  required
                />
              </div>
              <p className="text-xs text-slate-500">
                Min 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="pl-12 h-11 border-2 border-slate-200 focus:border-primary"
                  data-testid="register-confirm-password-input"
                  required
                />
              </div>
            </div>

            {/* Terms Agreement */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="terms-agreement"
                checked={formData.agreedToTerms}
                onCheckedChange={(checked) => setFormData({ ...formData, agreedToTerms: checked })}
                className="mt-0.5 h-5 w-5"
                data-testid="terms-agreement-checkbox"
              />
              <Label htmlFor="terms-agreement" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                I agree to the{" "}
                <a 
                  href="/terms-of-use" 
                  target="_blank" 
                  className="text-primary font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Use
                </a>{" "}
                and{" "}
                <a 
                  href="/privacy-policy" 
                  target="_blank" 
                  className="text-primary font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
                <span className="text-red-500">*</span>
              </Label>
            </div>

            {/* TCPA Transactional Consent (Required) */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="consent-transactional"
                checked={formData.consentTransactional}
                onCheckedChange={(checked) => setFormData({ ...formData, consentTransactional: checked })}
                className="mt-0.5 h-5 w-5"
                data-testid="consent-transactional-checkbox"
              />
              <Label htmlFor="consent-transactional" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                By checking this box, I consent to receive transactional messages from GigsEasy.com related to my account, such as account creation confirmations, task notifications, payment updates, and account status notifications.
                <span className="text-red-500">*</span>
              </Label>
            </div>

            {/* TCPA Marketing Consent (Optional) */}
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="consent-marketing"
                checked={formData.consentMarketing}
                onCheckedChange={(checked) => setFormData({ ...formData, consentMarketing: checked })}
                className="mt-0.5 h-5 w-5"
                data-testid="consent-marketing-checkbox"
              />
              <Label htmlFor="consent-marketing" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                By checking this box, I agree to receive marketing messages from GigsEasy.com, including promotional offers and updates. <span className="text-slate-500">(Optional)</span>
              </Label>
            </div>

            {/* TCPA Disclaimer */}
            <div className="text-xs text-slate-500 leading-relaxed p-3 bg-slate-100 rounded-lg">
              <p>
                * Consent to receive messages is not a condition for using GigsEasy.com services. Message and data rates may apply. You may receive up to 4 messages per month. You can opt out at any time by contacting{" "}
                <a href="mailto:support@gigseasy.com" className="text-primary hover:underline">support@gigseasy.com</a>. 
                All information collected during the opt-in process will not be sold, rented, or shared. View our{" "}
                <a href="/terms-of-use" target="_blank" className="text-primary hover:underline">Terms of Use</a>,{" "}
                <a href="/privacy-policy" target="_blank" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.agreedToTerms || !formData.consentTransactional}
              data-testid="register-submit-btn"
              className="w-full h-12 bg-primary text-white font-medium border-2 border-slate-900 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:-translate-y-1 transition-all active:translate-y-0 active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {loading ? "Creating account..." : "Create Account"}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-slate-500">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
            className="w-full h-12 border-2 border-slate-200 hover:border-slate-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-slate-600 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
